/**
 * The limited view (Dawn, 2026-11-02): a site Bread List built for a client is MANAGED, and an
 * invited team member can be a CONTRIBUTOR with picked areas. The gateway decides and refuses
 * (services/api-gateway/src/lib/limited-access.ts); this file only mirrors that decision so the
 * dashboard hides what would be refused and says why. GET /auth/me `limited_access` is the answer.
 *
 * One helper, like lib/htmlAccess.ts: every screen asks here, never works it out itself.
 */

/** The areas an owner can tick for a Contributor. Same keys as the gateway's PICKABLE_AREAS. */
export const LIMITED_AREAS = ['content', 'leads', 'ai_marketer', 'seo', 'business_info', 'analytics', 'requests'] as const;
export type LimitedArea = (typeof LIMITED_AREAS)[number];

export const AREA_LABELS: Record<LimitedArea, string> = {
  content: 'Agents, offices, listings, blog and communities',
  leads: 'Leads',
  ai_marketer: 'AI Marketer',
  seo: 'SEO Autopilot',
  business_info: 'Business info (contact details, social links)',
  analytics: 'Site stats',
  requests: 'Request changes from Bread List',
};

/** The invite form's quick picks (plan 6b). */
export const AREA_PRESETS: { id: string; label: string; areas: LimitedArea[] }[] = [
  { id: 'leads', label: 'Leads only', areas: ['leads'] },
  { id: 'marketing', label: 'Marketing (AI Marketer + SEO Autopilot)', areas: ['ai_marketer', 'seo'] },
  { id: 'all', label: 'Everything a limited user can do', areas: [...LIMITED_AREAS] },
];

export interface LimitedAccess {
  /** This person is limited on the active site (managed site, or a Contributor). */
  limited: boolean;
  /** The active site is managed (and this person is limited by it). */
  managed: boolean;
  contributor: boolean;
  areas: LimitedArea[];
  /** Money is the account owner's alone. */
  can_pay: boolean;
  /**
   * The ACCOUNT has a managed site, so its money rules (plans only go up; cancel by the owner's
   * emailed request) apply on every site of it, whichever is selected (review on #1112).
   */
  account_managed: boolean;
  /** May invite team members (Contributors only). */
  can_invite: boolean;
  /** The active site is managed, whoever is asking (staff in Test Mode see a banner). */
  site_managed: boolean;
  /** The site this answer is for (null: unknown, e.g. an older gateway). */
  site_id: string | null;
}

export const NOT_LIMITED: LimitedAccess = {
  limited: false,
  managed: false,
  contributor: false,
  areas: [...LIMITED_AREAS],
  can_pay: true,
  account_managed: false,
  can_invite: true,
  site_managed: false,
  site_id: null,
};

/**
 * While the active site's answer is still on its way (or could not be read): nothing but the
 * screens everyone may open, so a managed site never flashes as editable (Dawn, Sites#1108 review).
 */
export const PENDING_ACCESS: LimitedAccess = {
  limited: true,
  managed: false,
  contributor: false,
  areas: [],
  can_pay: false,
  account_managed: false,
  can_invite: false,
  site_managed: false,
  site_id: null,
};

/** The gateway's `limited_access`, defensively: anything unreadable is "not limited" for DISPLAY only. */
export function parseLimitedAccess(raw: unknown): LimitedAccess {
  if (!raw || typeof raw !== 'object') return NOT_LIMITED;
  const r = raw as Record<string, unknown>;
  const areas = Array.isArray(r.areas) ? LIMITED_AREAS.filter((a) => (r.areas as unknown[]).includes(a)) : [...LIMITED_AREAS];
  return {
    limited: r.limited === true,
    managed: r.managed === true,
    contributor: r.contributor === true,
    areas,
    can_pay: r.can_pay !== false,
    account_managed: r.account_managed === true,
    can_invite: r.can_invite !== false,
    site_managed: r.site_managed === true,
    site_id: typeof r.site_id === 'string' && r.site_id ? r.site_id : null,
  };
}

/**
 * What a dashboard screen belongs to:
 *   a LimitedArea   allowed when the person has it;
 *   any             every signed-in person (help, preview, own account settings);
 *   dashboard       the home page: everyone but a Contributor without Site stats;
 *   billing         the owner only;
 *   team            the team page (not a Contributor);
 *   blocked         design and structure: the Bread List team changes it.
 */
export type PageArea = LimitedArea | 'any' | 'dashboard' | 'billing' | 'team' | 'blocked';

/** The screen a path (and its search) belongs to. Unknown screens are blocked (default-deny). */
export function pageArea(pathname: string, search = ''): PageArea {
  const path = pathname.replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(search);
  const starts = (prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

  if (path === '/dashboard') return 'dashboard';
  if (starts('/help') || starts('/preview') || path === '/settings') return 'any';
  if (path === '/studio') return params.get('view') === 'preview' ? 'any' : 'blocked';
  if (path === '/studio/blog' || path === '/studio/communities' || path === '/offices' || path === '/agents' || path === '/footer-links' || starts('/listing-entry')) return 'content';
  if (path === '/team') return params.get('view') === 'agents' ? 'content' : 'team';
  if (path === '/settings/team') return 'team';
  if (starts('/leads') || path === '/settings/lead-routing') return 'leads';
  if (starts('/ai-marketer')) return 'ai_marketer';
  if (starts('/autopilot')) return 'seo';
  if (path === '/business-info' || path === '/account' || path === '/profile') return 'business_info';
  if (path === '/requests') return 'requests';
  if (path === '/billing' || path === '/ai-usage') return 'billing';
  return 'blocked';
}

/** May this person open a screen of this kind? Pure. */
export function pageAllowed(access: LimitedAccess, area: PageArea): boolean {
  if (!access.limited) return true;
  switch (area) {
    case 'any': return true;
    case 'dashboard': return !access.contributor || access.areas.includes('analytics');
    case 'billing': return access.can_pay;
    case 'team': return access.can_invite;
    case 'blocked': return false;
    default: return access.areas.includes(area);
  }
}

export function pathAllowed(access: LimitedAccess, to: string): boolean {
  const url = new URL(to, 'https://builder.local');
  return pageAllowed(access, pageArea(url.pathname, url.search));
}

const AREA_HOME: Record<LimitedArea, string> = {
  content: '/studio/blog',
  leads: '/leads',
  ai_marketer: '/ai-marketer',
  seo: '/autopilot',
  business_info: '/business-info',
  analytics: '/dashboard',
  requests: '/requests',
};

/** Where this person lands. A Contributor with one area opens straight on it (plan 6b). */
export function limitedHomePath(access: LimitedAccess): string {
  if (pageAllowed(access, 'dashboard')) return '/dashboard';
  const first = access.areas.find((a) => a !== 'requests') ?? access.areas[0];
  return first ? AREA_HOME[first] : '/help';
}

/** Can this person publish anything with Limited Publish? */
export function canPublishLimited(access: LimitedAccess): boolean {
  return access.areas.includes('content') || access.areas.includes('business_info');
}

/** Why a screen is closed, in the person's words. */
export function closedPageMessage(access: LimitedAccess, area: PageArea): { title: string; body: string } {
  if (area === 'billing') {
    return { title: 'Only the account owner can do this', body: 'Plans, payments and AI credits are managed by the owner of this account.' };
  }
  if (access.contributor && area !== 'blocked') {
    return { title: 'Not part of your access', body: 'Your team access does not include this. Ask the account owner to change your access.' };
  }
  if (access.managed) {
    return {
      title: 'Managed by Bread List',
      body: 'Bread List built this site, so its design, pages, menu, logo, colors, fonts and domain are changed by our team. Ask us and we will make the change for you.',
    };
  }
  return { title: 'Not part of your access', body: 'Ask the account owner to change your access.' };
}
